import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudIcon from '@mui/icons-material/CloudUpload';
import { APIContext } from '../Contexts/APIContext';
import { useContext, useId } from 'react';

interface IProps {
    onDrop: (newFiles: File[]) => void;
    onDelete: (fileName: string) => void;
    files: File[];
    acceptedTypes?: string;
}

const boxStyle = [{
    display: 'flex',
    p: 2,
    bgcolor: 'grey.200',
    width: '100%',
    height: '100%',
    border: '2px dashed grey',
    borderColor: 'grey.500',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center'
},
{
    '&:hover': {
        bgcolor: 'grey.300',
    },
}];

const fileSizeLimitBytes = 0.25 * 1024 * 1024 * 1024;

export default function MultiFileUpload(props: IProps) {
    const { onDrop, onDelete, files, acceptedTypes } = props;
    const { showMessage } = useContext(APIContext);
    const inputId = useId();

    const validateFileSize = (file: File) => {
        if (file.size >= fileSizeLimitBytes) {
            showMessage('warning', `${file.name} exceeds the 250MB upload limit.`);
            return false;
        }

        return true;
    };

    const handleNewFiles = (fileList: FileList) => {
        const incomingFiles = Array.from(fileList).filter(validateFileSize);
        if (incomingFiles.length > 0) {
            onDrop(incomingFiles);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            handleNewFiles(event.target.files);
        }
    };

    const dragEvents = {
        onDragEnter: (event: React.DragEvent<HTMLElement>) => {
            event.stopPropagation();
            event.preventDefault();
        },
        onDragLeave: (event: React.DragEvent<HTMLElement>) => {
            event.stopPropagation();
            event.preventDefault();
        },
        onDragOver: (event: React.DragEvent<HTMLElement>) => {
            event.stopPropagation();
            event.preventDefault();
        },
        onDrop: (event: React.DragEvent<HTMLElement>) => {
            event.stopPropagation();
            event.preventDefault();

            if (event.dataTransfer.files.length > 0) {
                handleNewFiles(event.dataTransfer.files);
            }
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography>Maximum file size 250MB.</Typography>
            </Grid>
            <Grid size={12}>
                <input
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    id={inputId}
                    type="file"
                    accept={acceptedTypes}
                    multiple
                />
                <label htmlFor={inputId} {...dragEvents}>
                    <Box sx={boxStyle}>
                        <Stack alignItems="center">
                            <CloudIcon color="action" />
                            <Typography>Click or Drag & Drop to Upload Files</Typography>
                            <Typography variant="body2">You may upload multiple files</Typography>
                        </Stack>
                    </Box>
                </label>
            </Grid>
            {files.length > 0 &&
                <Grid size={12}>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {files.map((file) => (
                            <Chip
                                key={file.name}
                                label={file.name}
                                onDelete={() => onDelete(file.name)}
                            />
                        ))}
                    </Stack>
                </Grid>
            }
        </Grid>
    );
}
